import { Box, DatePicker, Stack, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button } from '@components/Button';
import {
  parseCollectionFlagOptionValue,
  PREFIXED_COLLECTION_FLAG_TYPE_OPTIONS,
} from '@components/CollectionFlag/constants';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { SelectInput } from '@components/Select/SelectInput';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { createCollectionFlagAction } from '@store/collectionFlags/actions';
import { canFlagCollections } from '@store/collectionFlags/store';
import {
  CustomerCreditReviewFlag,
  customerDetailsStore,
} from '@store/customers/customerDetails';
import {
  createCustomerCreditFlagAction,
  deleteCustomerCreditFlagAction,
  updateCustomerCreditFlagAction,
} from '@store/customers/customerDetails/LTL';
import { closeModal, isModalOpen } from '@store/modals';
import { OperationType } from '@typeDefinitions/operationTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { DateTime } from 'luxon';
import { Component, createEffect, createMemo, on, Show } from 'solid-js';
import * as yup from 'yup';

import {
  ASSIGNED_DEPARTMENT_OPTIONS,
  buildFlagTypeOptions,
  canUseFlagType,
  CustomerFlagType,
  isFinancialExposureFlag,
} from './customerFlagConstants';

const FINANCIAL_EXPOSURE_FLAG_VALUE = String(
  CustomerFlagType.FlagForFinancialExposure,
);

/**
 * Validation mirrors the BE, which is conditional per flag type:
 * Flag for Review needs a reason, a review date and a department; Financial
 * Exposure needs the reason only.
 *
 * `reason` is the label the designs use for the BE's `description` column —
 * there is only ever one free-text field.
 */
const isCollectionFlagValue = (flagType?: string) =>
  parseCollectionFlagOptionValue(flagType) !== undefined;

const skipsReviewFields = (flagType?: string) =>
  flagType === FINANCIAL_EXPOSURE_FLAG_VALUE || isCollectionFlagValue(flagType);

const customerFlagSchema = yup.object({
  flagType: yup.string().required('Flag Type is required'),
  description: yup.string().when('flagType', {
    is: isCollectionFlagValue,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('Reason is required'),
  }),
  reviewDate: yup.string().when('flagType', {
    is: skipsReviewFields,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('Review Date is required'),
  }),
  assignedDepartment: yup.string().when('flagType', {
    is: skipsReviewFields,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('Assigned Department is required'),
  }),
});

type CustomerFlagFormValues = {
  flagType: string;
  reviewDate?: string;
  assignedDepartment?: string;
  description: string;
};

/** Felte surfaces field errors as `string[] | null`; the inputs take a string. */
const firstError = (error?: string | string[] | null): string | undefined => {
  if (!error) return undefined;
  return Array.isArray(error) ? error.join(', ') : String(error);
};

export const CustomerFlagModal: Component = () => {
  const isEditMode = () =>
    customerDetailsStore.customerFlagReviewModalModeAndLoading.mode === 'edit';
  const selectedFlag = () =>
    customerDetailsStore.selectedCustomerFlagReviewData;

  const flagTypeOptions = createMemo(() => [
    ...buildFlagTypeOptions(
      customerDetailsStore.customerFlagTypes,
      customerDetailsStore.customerFlagPermissions,
    ),
    ...(canFlagCollections() && !isEditMode()
      ? PREFIXED_COLLECTION_FLAG_TYPE_OPTIONS
      : []),
  ]);

  const { data, setData, form, errors, isSubmitting, setIsSubmitting, reset } =
    createForm<CustomerFlagFormValues>({
      initialValues: {
        flagType: '',
        reviewDate: undefined,
        assignedDepartment: undefined,
        description: '',
      },
      extend: validator({ schema: customerFlagSchema }),
      onSubmit: async (values) => {
        try {
          setIsSubmitting(true);
          const collectionFlagType = parseCollectionFlagOptionValue(
            values.flagType,
          );
          if (collectionFlagType && !isEditMode()) {
            const isCreated = await createCollectionFlagAction(
              {
                customerId: customerDetailsStore.customer.id!,
                flagType: collectionFlagType,
                description: values.description.trim(),
                operationType: 'Insert',
              },
              CHANGE_TRACKING_SOURCES.customerDetails
                .headerCollectionFlagCreate,
            );
            if (isCreated) {
              closeModal('flagForReviewModalId');
            }
            return;
          }
          const isFinancialExposure =
            values.flagType === FINANCIAL_EXPOSURE_FLAG_VALUE;

          const sharedFields = {
            description: values.description,
            assignedDepartment: isFinancialExposure
              ? null
              : Number(values.assignedDepartment),
            reviewDate: isFinancialExposure ? null : values.reviewDate!,
          };

          const isSuccess = isEditMode()
            ? await updateCustomerCreditFlagAction({
                ...selectedFlag(),
                ...sharedFields,
                operationType: OperationType.Update,
                actionType: 'Update',
              } as CustomerCreditReviewFlag)
            : await createCustomerCreditFlagAction({
                ...sharedFields,
                operationType: OperationType.Insert,
                customerId: customerDetailsStore.customer.id!,
                flagType: Number(values.flagType),
              } as CustomerCreditReviewFlag);

          if (isSuccess) {
            closeModal('flagForReviewModalId');
          }
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  const isFinancialExposureSelected = () =>
    data().flagType === FINANCIAL_EXPOSURE_FLAG_VALUE;

  /** Review Date and Assigned Department only apply to Flag for Review. */
  const isCollectionSelected = () => isCollectionFlagValue(data().flagType);

  const showReviewFields = () =>
    Boolean(data().flagType) &&
    !isFinancialExposureSelected() &&
    !isCollectionSelected();

  /** A user can only edit a flag of a type their feature flags allow. */
  const canSubmit = () =>
    !isEditMode() ||
    canUseFlagType(
      selectedFlag()?.flagType,
      customerDetailsStore.customerFlagPermissions,
    );

  const canDeleteFlag = () =>
    canSubmit() && (selectedFlag()?.canDelete ?? false);

  createEffect(
    on(
      () => isModalOpen('flagForReviewModalId'),
      (isOpen, wasOpen) => {
        if (!isOpen || wasOpen) return;

        if (!isEditMode()) {
          reset();
          return;
        }

        const flag = selectedFlag();
        if (!flag) return;

        setData('flagType', String(flag.flagType));
        setData('description', flag.description);

        if (isFinancialExposureFlag(flag.flagType)) {
          setData('reviewDate', undefined);
          setData('assignedDepartment', undefined);
          return;
        }

        let reviewDateValue: string | undefined;
        const reviewDateString = flag.reviewDate;
        if (Boolean(reviewDateString)) {
          const parsedDate = DateTime.fromFormat(
            reviewDateString!,
            'MM/dd/yyyy',
            { zone: 'utc' },
          );
          if (parsedDate.isValid) {
            reviewDateValue = parsedDate.toLocal().toISO()!;
          }
        }
        setData('reviewDate', reviewDateValue);
        setData(
          'assignedDepartment',
          flag.assignedDepartment === null ||
            flag.assignedDepartment === undefined
            ? undefined
            : String(flag.assignedDepartment),
        );
      },
    ),
  );

  return (
    <form ref={form}>
      <Stack spacing={1.5} width={900} maxWidth={'90vw'}>
        <Show when={!isEditMode()}>
          <Box display="flex" gap={1.5} alignItems="flex-start" mt={2}>
            <SelectInput
              required
              label="Flag Type"
              placeholder="Select a Flag Type"
              menuItems={flagTypeOptions()}
              value={data().flagType}
              onChange={(value) => setData('flagType', value)}
              error={firstError(errors().flagType)}
              dataTestId={formatDataTestId('Flag Type', 'selectInput')}
            />
          </Box>
        </Show>

        <Show when={showReviewFields()}>
          <Box display="flex" gap={1.5} alignItems="flex-start" mb={1} mt={2}>
            <DatePicker
              required
              label="Review date"
              value={data().reviewDate}
              handleChange={(date) => {
                setData('reviewDate', date);
              }}
              error={firstError(errors().reviewDate)}
              dataTestId="customer-flag-review-date-datepicker"
            />
            <SelectInput
              required
              label="Assigned Department"
              placeholder="Select a Department"
              menuItems={ASSIGNED_DEPARTMENT_OPTIONS}
              value={data().assignedDepartment ?? ''}
              onChange={(value) => setData('assignedDepartment', value)}
              error={firstError(errors().assignedDepartment)}
              dataTestId={formatDataTestId(
                'Assigned Department',
                'selectInput',
              )}
            />
          </Box>
        </Show>

        <TextField
          label={isCollectionSelected() ? 'Description' : 'Reason'}
          required={!isCollectionSelected()}
          multiline
          rows={4}
          value={data().description}
          onChange={(value: string) => setData('description', value)}
          error={firstError(errors().description)}
          dataTestId="customer-flag-reason-textInput"
        />

        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Show when={isEditMode()}>
            <Button
              disabled={isSubmitting() || !canSubmit()}
              isLoading={isSubmitting()}
              type="submit"
              label="Update Flag"
              variant="contained"
              dataTestId="customer-details-update-flag-button"
            />
            <Show when={canDeleteFlag()}>
              <Button
                type="button"
                disabled={
                  customerDetailsStore.customerFlagReviewModalModeAndLoading
                    .loading
                }
                isLoading={
                  customerDetailsStore.customerFlagReviewModalModeAndLoading
                    .loading
                }
                label="Remove Flag"
                variant="contained"
                class="bg-[#B00020]!"
                dataTestId="customer-details-remove-flag-button"
                onClick={async () => {
                  const flag = selectedFlag();
                  if (!flag) return;
                  const isSuccess = await deleteCustomerCreditFlagAction(
                    flag.id!,
                  );
                  if (Boolean(isSuccess)) {
                    closeModal('flagForReviewModalId');
                  }
                }}
              />
            </Show>
          </Show>
          <Show when={!isEditMode()}>
            <Button
              disabled={isSubmitting()}
              isLoading={isSubmitting()}
              type="submit"
              label="Save"
              variant="contained"
              dataTestId="customer-details-add-flag-button"
            />
          </Show>
          <Button
            type="button"
            label="Cancel"
            variant="text"
            onClick={() => closeModal('flagForReviewModalId')}
            dataTestId="customer-details-cancel-flag-button"
          />
        </Box>
      </Stack>
    </form>
  );
};
